import {Injectable} from "@angular/core";
import {PersonalTaskResponse, TaskResponse} from "../../models/task-response";
import {Constants, MapOfWorkflowStatus} from "../../../../models/constants";
import {WorkflowTaskStatusTypeIdState} from "../../../../models/custom-models/workflow-task-status-type-id";
import {UserConsentDialogComponent} from "../../../../components/Shared/user-consent-dialog/user-consent-dialog.component";
import {UpdateTaskConstants} from "../models/update-task-model.model";
import {MatDialog} from "@angular/material/dialog";
import {SharedService} from "../../../../services/shared.service";
import {AccessDomain} from "../../../../models/access-domain";
import {RoleAction} from "../../../../models/custom-models/role-action";
import {RoleActionsService} from "../../../shared/Services/role-actions.service";
import {GetTaskNoDialogBoxComponent} from "../../../../components/Shared/get-task-no-dialogbox/get-task-no-dialog-box.component";
import {
    OpenTaskRequestModel,
    UpdateTaskDialogConstants,
} from "../../../shared/Models/task.model";
import {Observable} from "rxjs";
import {format} from "date-fns";
import {UserAccount} from "../../../../models/user-account";
import {User} from "../../../../models/user";

@Injectable({
    providedIn: "root",
})
export class WorkItemUtilsService {
    allowedRolesForPerformanceNotes = Constants.Task.ALLOWED_ROLES_FOR_PERFORMANCE_NOTES;
    private userAccountIdForTask: number;

    constructor(
        private dialog: MatDialog,
        private sharedService: SharedService,
        private roleActionsService: RoleActionsService,
    ) {
    }

    findAllDefaultWorkflowTaskStatus(task: TaskResponse, allWorkflowStatus: WorkflowTaskStatusTypeIdState[]) {
        if (task.taskWorkflowId != null) {
            const indicesToRemove = new Set(
                MapOfWorkflowStatus.getWorkflowStatusCodes(
                    task.fkWorkflowTaskStatus?.workflowTaskStatus!,
                    task.taskActStDate !== null,
                ),
            );
            let filteredByWorkflowType: WorkflowTaskStatusTypeIdState[];

            // If workflow is NOT personal or team → force TEAM workflow
            if (
                task.taskWorkflowId !== Constants.WorkFlowType.PERSONAL_WORK_ITEM &&
                task.taskWorkflowId !== Constants.WorkFlowType.TEAM_WORK_ITEM
            ) {
                filteredByWorkflowType = allWorkflowStatus.filter(
                    status => status.workflowTypeId === Constants.WorkFlowType.TEAM_WORK_ITEM
                );
            } else {
                // Otherwise use task’s workflow
                filteredByWorkflowType = allWorkflowStatus.filter(
                    status => status.workflowTypeId === task.taskWorkflowId
                );
            }
            return filteredByWorkflowType.filter((_, index) => indicesToRemove.has(index));
        } else {
            return [];
        }
    }

    // Deleting a work item via AI chat is on hold for now, It will be implemented in Soon.
    deleteTask(task: TaskResponse, isFromChatScreen: boolean = false) {
        const dialogRef = this.dialog.open(UserConsentDialogComponent, {
            maxWidth: "20vw",
            panelClass: "confirmation-dialog-container",
            data: {
                title: UpdateTaskConstants.DELETE_TASK_TITLE + " #" + task.taskIdentifier,
                message: UpdateTaskConstants.DELETE_TASK_CONFIRMATION,
            },
        });
        dialogRef.afterClosed().subscribe((result: string) => {
            if (result == "true") {
                let userAccountIds = this.sharedService.getUserAccountIdWithOrgId(task.fkOrgId.orgId);
                if (userAccountIds) {
                    // this.taskService.deleteTask(task.taskId, String(userAccountIds)).subscribe({
                    //     next: () => {
                    //         isFromChatScreen
                    //             ? this.chatCommandService.addChatConversation(
                    //                   new ChatCommands(
                    //                       UpdateTaskConstants.DELETE_TASK_SUCCESS_MESSAGE,
                    //                       ChatCommandsEnum.aiInformationalMessage,
                    //                   ),
                    //               )
                    //             : this.toastr.success(UpdateTaskConstants.DELETE_TASK_SUCCESS_MESSAGE);
                    //     },
                    //     error: (error: HttpErrorResponse) => {
                    //         let message = this.handlerErrorService.extractErrorMessage(error);
                    //         isFromChatScreen
                    //             ? this.chatCommandService.addChatConversation(
                    //                   new ChatCommands(message, ChatCommandsEnum.aiInformationalMessage),
                    //               )
                    //             : this.handlerErrorService.handleError(error);
                    //     },
                    // });
                }
            }
        });
    }

    isUserAllowedToEditNotes(teamId: number): boolean {
        let isAllowed = false;
        let accessDomains = this.sharedService.getLoggedInUserAccessDomains();
        let roleActions = this.roleActionsService.getRoleActions();
        accessDomains
            .filter((accessDomain: AccessDomain) => {
                return accessDomain.entityTypeId === Constants.EntityTypes.TEAM && accessDomain.entityId == teamId;
            })
            .forEach((accessDomain: AccessDomain) => {
                roleActions.forEach((roleAction: RoleAction) => {
                    if (
                        (roleAction.roleId == accessDomain.roleId &&
                            (roleAction.actionId == Constants.ActionId.ALL_TASK_ESSENTIAL_UPDATE ||
                                roleAction.actionId == Constants.ActionId.TASK_ESSENTIAL_UPDATE)) ||
                        roleAction.actionId == Constants.ActionId.ALL_TASK_BASIC_UPDATE ||
                        roleAction.actionId == Constants.ActionId.TASK_BASIC_UPDATE
                    ) {
                        isAllowed = true;
                    }
                });
            });
        return isAllowed;
    }

    isHighPriorityTask(priority: string): boolean {
        return [Constants.PriorityConstants.PRIORITY_P0, Constants.PriorityConstants.PRIORITY_P1].includes(priority);
    }

    isNormalORLowPriorityTask(priority: string): boolean {
        return [
            Constants.PriorityConstants.PRIORITY_P2,
            Constants.PriorityConstants.PRIORITY_P3,
            Constants.PriorityConstants.PRIORITY_P4,
        ].includes(priority);
    }

    openGetTaskNumberDialogAndOpenUpdateTaskDialog() {
        const dialogRef = this.dialog.open(GetTaskNoDialogBoxComponent, {
            disableClose: false,
            autoFocus: false,
            minWidth: "25vw",
            maxWidth: "90vw",
        });
        dialogRef.afterClosed().subscribe(async (event: OpenTaskRequestModel | number | null | undefined) => {
            if (event && event instanceof OpenTaskRequestModel) {
                const { WorkItemDialogComponent } = await import(
                    "../work-item-dialog/work-item-dialog.component"
                );
                this.dialog
                    .open(WorkItemDialogComponent, {
                        disableClose: true,
                        minWidth: UpdateTaskDialogConstants.minWidth,
                        maxWidth: UpdateTaskDialogConstants.maxWidth,
                        height: UpdateTaskDialogConstants.height,
                        panelClass: UpdateTaskDialogConstants.panelClass,
                        data: {
                            taskNumber: event.taskIdentifier,
                            isTaskFromTaskBoard: false,
                            openTaskRequestModel: event,
                        },
                        autoFocus: false,
                    })
                    .afterClosed()
                    .subscribe();
            }

            if (event && typeof event === "number") {
                import("../../personal-task/personal-update-task/personal-update-task.component").then(
                    ({PersonalUpdateTaskComponent}) => {
                        this.dialog.open(PersonalUpdateTaskComponent, {
                            disableClose: true,
                            width: "60vw",
                            data: {taskNumber: event},
                            autoFocus: false,
                        });
                    });
            }
        });
    }

    isPersonalTask(taskTypeId?: number, teamId?: number | string | null, isPersonalTask: boolean = false): boolean {
        return taskTypeId === Constants.TaskTypes.PERSONAL_TASK || isPersonalTask || teamId === 0;
    }

    navigateToPersonalTaskDetailsPage(
        taskNumber: string | number,
    ): Observable<PersonalTaskResponse | null | undefined> {
        return new Observable<PersonalTaskResponse | null | undefined>((observer) => {
            import("../../personal-task/personal-update-task/personal-update-task.component")
                .then(({PersonalUpdateTaskComponent}) => {
                    const dialogRef = this.dialog.open(PersonalUpdateTaskComponent, {
                        disableClose: true,
                        width: "60vw",
                        data: {taskNumber: taskNumber},
                        autoFocus: false,
                    });

                    dialogRef.afterClosed().subscribe((result) => {
                        observer.next(result);
                        observer.complete();
                    });
                })
                .catch((error) => {
                    observer.error(error);
                });
        });
    }

    setUserAccountIdForTask(userAccountId: number) {
        this.userAccountIdForTask = userAccountId;
    }

    getUserAccountIdForTask() {
        return this.userAccountIdForTask;
    }

    showPerfNotesOption(orgTask: TaskResponse, perfFeedbacksLength: number, currentAccountId?: number): boolean {
        if (!orgTask) {
            return false;
        }
        const assignedAccountIdExists = orgTask.fkAccountIdAssigned?.accountId;

        if (!assignedAccountIdExists) {
            return false;
        }
        const workflowTaskStatus = orgTask.fkWorkflowTaskStatus?.workflowTaskStatus?.toLowerCase() || "";

        const isTaskInBacklog = workflowTaskStatus.includes(Constants.WorkflowTaskStatusNamesLowerCase.BACKLOG);

        if (isTaskInBacklog) {
            return false;
        }

        const teamId = orgTask.fkTeamId?.teamId;

        return (
            (orgTask.fkAccountIdAssigned?.accountId === currentAccountId && perfFeedbacksLength > 0) ||
            [
                orgTask.fkAccountIdMentor1,
                orgTask.fkAccountIdMentor2,
                orgTask.fkAccountIdObserver1,
                orgTask.fkAccountIdObserver2,
            ].some((account) => account?.accountId === currentAccountId) ||
            (teamId ? this.checkAllowedRolesForPerformanceFeedbackInTeam(teamId) : false)
        );
    }

    checkAllowedRolesForPerformanceFeedbackInTeam(teamId: number): boolean {
        return this.sharedService
            .getLoggedInUserAccessDomains()
            ?.some(
                (accessDomain: AccessDomain) =>
                    accessDomain.entityTypeId === Constants.EntityTypes.TEAM &&
                    accessDomain.entityId == teamId &&
                    this.allowedRolesForPerformanceNotes?.includes(accessDomain.roleId),
            );
    }

    // openUpdateWorkItemDialog(dialogData: OpenWorkItemDialogData) {
    //     if (
    //         dialogData.openTaskRequestModel &&
    //         dialogData.openTaskRequestModel.taskNumber &&
    //         dialogData.openTaskRequestModel.teamId
    //     ) {
    //         this.dialog.open(WorkItemDialogComponent, {
    //             disableClose: true,
    //             minWidth: UpdateTaskDialogConstants.minWidth,
    //             maxWidth: UpdateTaskDialogConstants.maxWidth,
    //             height: UpdateTaskDialogConstants.height,
    //             panelClass: UpdateTaskDialogConstants.panelClass,
    //             data: dialogData,
    //         });
    //     }
    // }

    convertDateTimeIntoBackendDateFormat(dateToSet: Date | null) {
        if (dateToSet && !isNaN(dateToSet.getTime())) {
            return format(dateToSet, "yyyy-MM-dd'T'HH:mm:ss");
        } else {
            return null;
        }
    }

    convertDateTimeIntoBackendTimeFormat(dateToSet: Date | null) {
        if (dateToSet && !isNaN(dateToSet.getTime())) {
            return format(dateToSet, "HH:mm:ss");
        } else {
            return null;
        }
    }

    createUserAccountObject(userAccountId: number) {
        let userAccount = new UserAccount();
        userAccount.accountId = userAccountId;
        userAccount.email = null;
        userAccount.isActive = null;
        userAccount.isDefault = null;
        userAccount.orgId = null;
        userAccount.fkUserId = {} as User;
        return userAccount;
    }
}
